import { TrainerProfileLevelOptions } from '@/configs'
import type {
  TrainerCommissionBand,
  TrainerCommissionCondition,
  TrainerCommissionConditionDetail,
  TrainerCommissionContractTypeApi,
  TrainerCommissionRoleContractEntity,
  TrainerCommissionRuleEntity,
  TrainerCommissionSessionStatusApi,
  TrainerCommissionStatus,
  TrainerConductConditionEntity,
} from './model'
import { TrainerCommissionConductType, TrainerConductRateType } from './model'

const LEVEL_VALUE_TO_LABEL = Object.fromEntries(
  TrainerProfileLevelOptions.map((o) => [o.value, o.label])
) as Record<string, string>

const SESSION_API_TO_FORM: Record<TrainerCommissionSessionStatusApi, string> = {
  SHOW: 'Show',
  NO_SHOW: 'No show',
}

/**
 * API → user-visible display label. Used by list and detail rendering.
 * Display strings can be anything (matches `t('contract-permanent')` etc.).
 */
const CONTRACT_API_TO_DISPLAY: Record<TrainerCommissionContractTypeApi, string> = {
  PERMANENT: 'Full Time',
  FREELANCE: 'Freelance',
}

/**
 * API → form value used by `CreateCommissionConditionForm`. Must match the
 * `value` keys in `COMMISSION_CONTRACT_TYPE_OPTIONS` (see
 * `features/trainer-commission/constants.ts`) so that the reverse mapper in
 * `mapCommissionFormToRuleDto.FORM_CONTRACT_TO_API` can recognize them on
 * submit. If these diverge, edit-then-save silently drops `contractTypes`.
 */
const CONTRACT_API_TO_FORM_VALUE: Record<TrainerCommissionContractTypeApi, string> = {
  PERMANENT: 'Permanent',
  FREELANCE: 'Freelance',
}

function mapTrainerLevelToDisplay(level: string): string {
  return LEVEL_VALUE_TO_LABEL[level] ?? level
}

function mapSessionStatusToDisplay(s: TrainerCommissionSessionStatusApi): string {
  return SESSION_API_TO_FORM[s] ?? s
}

function mapContractTypeToDisplay(c: TrainerCommissionContractTypeApi): string {
  return CONTRACT_API_TO_DISPLAY[c] ?? c
}

function mapContractTypeToFormValue(c: TrainerCommissionContractTypeApi): string {
  return CONTRACT_API_TO_FORM_VALUE[c] ?? c
}

function mapRoleContractsToFormRows(
  roleContracts: TrainerCommissionRoleContractEntity[]
): { roles: number[]; contractTypes: string[] }[] {
  const groups = new Map<string, { roles: number[]; types: TrainerCommissionContractTypeApi[] }>()
  for (const rc of roleContracts ?? []) {
    const types = [...(rc.contractTypes ?? [])].sort()
    const key = types.join(',')
    if (!groups.has(key)) {
      groups.set(key, { roles: [], types })
    }
    const g = groups.get(key)!
    g.roles.push(rc.role.id)
  }
  return [...groups.values()].map(({ roles, types }) => ({
    roles: [...new Set(roles)],
    // Form prefill must use form VALUES (matching the Autocomplete options'
    // value space), not display labels. Otherwise `FORM_CONTRACT_TO_API` in
    // mapCommissionFormToRuleDto.ts can't reverse-map them on save.
    contractTypes: types.map(mapContractTypeToFormValue),
  }))
}

function conditionToBand(row: TrainerConductConditionEntity): TrainerCommissionBand {
  const from = row.sessionCountFrom
  const conditionLabel =
    row.sessionCountTo === null || row.sessionCountTo === undefined
      ? `Sessions ${from}+ sessions`
      : `Sessions ${from}-${row.sessionCountTo} sessions`

  const rate = Number(row.rate)
  if (row.rateType === TrainerConductRateType.PERCENT) {
    return {
      conditionLabel,
      conductRatePercent: rate,
      conductFeeThb: null,
    }
  }
  return {
    conditionLabel,
    conductRatePercent: null,
    conductFeeThb: rate,
  }
}

function sortConditions(rows: TrainerConductConditionEntity[]): TrainerConductConditionEntity[] {
  return [...rows].sort((a, b) => {
    const oa = a.orderIndex ?? 0
    const ob = b.orderIndex ?? 0
    if (oa !== ob) return oa - ob
    return a.sessionCountFrom - b.sessionCountFrom
  })
}

export function mapRuleEntityToCondition(
  entity: TrainerCommissionRuleEntity
): TrainerCommissionCondition {
  const roleNames = (entity.roleContracts ?? [])
    .map((rc) => rc.role?.name)
    .filter((n): n is string => Boolean(n))

  const contractTypeSet = new Set<string>()
  for (const rc of entity.roleContracts ?? []) {
    for (const ct of rc.contractTypes ?? []) {
      contractTypeSet.add(mapContractTypeToDisplay(ct))
    }
  }

  const status: TrainerCommissionStatus = entity.isActive ? 'ACTIVE' : 'INACTIVE'
  const conditionKind: 'training' | 'class' =
    entity.conductType === TrainerCommissionConductType.CLASS ? 'class' : 'training'

  return {
    id: entity.id,
    status,
    commissionRuleName: entity.name ?? '',
    roles: roleNames,
    trainerLevels: (entity.trainerLevels ?? []).map(mapTrainerLevelToDisplay),
    sessionStatuses: (entity.sessionStatus ?? []).map(mapSessionStatusToDisplay),
    trainingProgramIds: (entity.trainingPrograms ?? []).map((p) => p.id),
    trainingProgramNames: (entity.trainingPrograms ?? []).map((p) => p.nameEn),
    contractType: [...contractTypeSet],
    conditionKind,
    classCategories: (entity.classCategories ?? []).map((c) => c.name),
  }
}

export function mapRuleEntityToDetail(
  entity: TrainerCommissionRuleEntity
): TrainerCommissionConditionDetail {
  const base = mapRuleEntityToCondition(entity)
  const sorted = sortConditions(entity.conditions ?? [])
  const commissionBands = sorted.map(conditionToBand)

  return {
    ...base,
    isActive: entity.isActive,
    commissionBands,
    conditions: sorted,
    classCategoryIds: (entity.classCategories ?? []).map((c) => c.id),
    roleContractRowsForEdit: mapRoleContractsToFormRows(entity.roleContracts ?? []),
  }
}
